import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Building2, Warehouse, MapPin, Plus, Edit2, AlertTriangle, Power, PowerOff, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TYPE_ICON = { store: Building2, warehouse: Warehouse, yard: MapPin, site: MapPin };

const blankLoc = {
  code: "",
  name: "",
  type: "store",
  address_line1: "",
  city: "",
  province: "",
  postal_code: "",
  manager_name: "",
  contact_phone: "",
  is_default: false,
  sells_to_public: true,
  active: true,
};

export default function Locations() {
  const [moduleStatus, setModuleStatus] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [editLoc, setEditLoc] = useState(null); // null = closed, {} = create

  const refresh = async () => {
    setLoading(true);
    try {
      const [statusRes, locRes] = await Promise.all([
        axios.get(`${API}/locations/module/status`),
        axios.get(`${API}/locations?active_only=false`),
      ]);
      setModuleStatus(statusRes.data);
      setLocations(locRes.data.locations || []);
    } catch (e) {
      toast.error("Failed to load locations: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const enableModule = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/locations/module/enable`);
      const { seeded_locations = [], migrated_products = 0 } = res.data;
      toast.success(
        `Warehouse module enabled. Seeded: ${seeded_locations.join(", ") || "none (already present)"}. ` +
        `Migrated ${migrated_products} products to MAIN.`,
        { duration: 6000 }
      );
      setEnableDialogOpen(false);
      await refresh();
    } catch (e) {
      toast.error("Enable failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const disableModule = async () => {
    if (!window.confirm(
      "Disable the Warehouse module?\n\n" +
      "The UI will be hidden everywhere but ALL location data is preserved. " +
      "You can re-enable at any time without re-migration."
    )) return;
    setBusy(true);
    try {
      await axios.post(`${API}/locations/module/disable`);
      toast.success("Warehouse module disabled. Data preserved.");
      await refresh();
    } catch (e) {
      toast.error("Disable failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveLocation = async (loc) => {
    setBusy(true);
    try {
      if (loc.id) {
        await saveWithRetry(() => axios.patch(`${API}/locations/${loc.id}`, loc));
        toast.success("Location updated");
      } else {
        await saveWithRetry(() => axios.post(`${API}/locations`, loc));
        toast.success("Location created");
      }
      setEditLoc(null);
      await refresh();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (loc) => {
    if (!window.confirm(`Deactivate "${loc.name}"?\n\nStock data is preserved; you can reactivate later.`)) return;
    try {
      await axios.delete(`${API}/locations/${loc.id}`);
      toast.success("Location deactivated");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const enabled = !!moduleStatus?.enabled;

  return (
    <div className="p-6 space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Warehouse className="h-6 w-6 text-purple-600" />
            Warehouse &amp; Locations
          </h1>
          <p className="text-gray-500">
            Multi-location stock master. The module is currently
            <span className={`ml-1 font-semibold ${enabled ? "text-green-600" : "text-gray-500"}`}>
              {enabled ? "ENABLED" : "DISABLED"}
            </span>.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={busy}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Master Toggle Card */}
      <Card className={enabled ? "border-green-300 bg-green-50/40" : "border-amber-300 bg-amber-50/40"}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {enabled ? <Power className="h-5 w-5 text-green-600" /> : <PowerOff className="h-5 w-5 text-amber-600" />}
            Module Master Switch
          </CardTitle>
          <CardDescription>
            {enabled
              ? "Warehouse UI is visible across the system. Disabling preserves all data."
              : "Module is OFF. The POS behaves as a single-location store with no warehouse UI anywhere."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            {enabled ? (
              <Button variant="destructive" onClick={disableModule} disabled={busy} data-testid="disable-module-btn">
                <PowerOff className="h-4 w-4 mr-2" /> Disable Module
              </Button>
            ) : (
              <Button onClick={() => setEnableDialogOpen(true)} disabled={busy} data-testid="enable-module-btn">
                <Power className="h-4 w-4 mr-2" /> Enable Module
              </Button>
            )}
            <div className="text-sm text-gray-600">
              Locations: <span className="font-mono font-bold">{moduleStatus?.location_count || 0}</span> ·
              Stock split rows: <span className="font-mono font-bold">{moduleStatus?.stock_split_count || 0}</span> ·
              Migrated: <span className="font-mono font-bold">{moduleStatus?.migrated ? "yes" : "no"}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Locations Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Locations ({locations.length})</CardTitle>
            <Button
              onClick={() => setEditLoc({ ...blankLoc })}
              disabled={!enabled}
              title={!enabled ? "Enable the module first" : ""}
              data-testid="add-location-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Location
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!enabled ? (
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" />
              Enable the module above to manage locations.
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No locations yet. Click "Add Location" to create one.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead className="text-center">Default</TableHead>
                  <TableHead className="text-center">Sells to Public</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((loc) => {
                  const Icon = TYPE_ICON[loc.type] || MapPin;
                  return (
                    <TableRow key={loc.id} data-testid={`location-row-${loc.code}`}>
                      <TableCell className="font-mono font-semibold">{loc.code}</TableCell>
                      <TableCell className="font-medium flex items-center gap-2">
                        <Icon className="h-4 w-4 text-purple-600" />
                        {loc.name}
                      </TableCell>
                      <TableCell className="capitalize">{loc.type}</TableCell>
                      <TableCell>{loc.city || "—"}</TableCell>
                      <TableCell>{loc.manager_name || "—"}</TableCell>
                      <TableCell className="text-center">{loc.is_default ? "✓" : ""}</TableCell>
                      <TableCell className="text-center">{loc.sells_to_public ? "✓" : ""}</TableCell>
                      <TableCell className="text-center">
                        <span className={loc.active ? "text-green-600" : "text-gray-400"}>
                          {loc.active ? "✓" : "✗"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditLoc(loc)} data-testid={`edit-location-${loc.code}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {loc.active && !loc.is_default && (
                          <Button variant="ghost" size="sm" onClick={() => deactivate(loc)} className="text-red-600">
                            Deactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enable confirmation dialog */}
      <Dialog open={enableDialogOpen} onOpenChange={setEnableDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Enable Warehouse Module?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-sm">
              <div>This will:</div>
              <ul className="list-disc pl-6 space-y-1">
                <li>Seed 3 default locations: <strong>MAIN</strong>, <strong>WH</strong>, <strong>BR01</strong></li>
                <li>Tag every existing product's current stock to <strong>MAIN</strong> (zero downtime)</li>
                <li>Reveal warehouse menus in the sidebar</li>
              </ul>
              <div className="pt-2 text-amber-700">
                <strong>The data structure is permanent.</strong> You can hide the UI again by disabling the toggle,
                but the per-location records will remain in the database.
              </div>
              <div className="pt-2 text-gray-600">
                Existing GRVs, sales, and reports remain unchanged.
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnableDialogOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={enableModule} disabled={busy} data-testid="confirm-enable-module">
              {busy ? "Enabling…" : "Yes, Enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Location dialog */}
      <Dialog open={!!editLoc} onOpenChange={(v) => !v && setEditLoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editLoc?.id ? "Edit Location" : "Add Location"}</DialogTitle>
          </DialogHeader>
          {editLoc && (
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Code *</Label>
                <Input
                  value={editLoc.code}
                  onChange={(e) => setEditLoc({ ...editLoc, code: e.target.value.toUpperCase() })}
                  placeholder="WH"
                  data-testid="loc-code-input"
                />
              </div>
              <div>
                <Label>Name *</Label>
                <Input
                  value={editLoc.name}
                  onChange={(e) => setEditLoc({ ...editLoc, name: e.target.value })}
                  placeholder="Warehouse / DC"
                  data-testid="loc-name-input"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={editLoc.type} onValueChange={(v) => setEditLoc({ ...editLoc, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="store">Store</SelectItem>
                    <SelectItem value="warehouse">Warehouse / DC</SelectItem>
                    <SelectItem value="yard">Yard</SelectItem>
                    <SelectItem value="site">Building Site</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Manager Name</Label>
                <Input value={editLoc.manager_name || ""} onChange={(e) => setEditLoc({ ...editLoc, manager_name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Address Line 1</Label>
                <Input value={editLoc.address_line1 || ""} onChange={(e) => setEditLoc({ ...editLoc, address_line1: e.target.value })} />
              </div>
              <div>
                <Label>City</Label>
                <Input value={editLoc.city || ""} onChange={(e) => setEditLoc({ ...editLoc, city: e.target.value })} />
              </div>
              <div>
                <Label>Province</Label>
                <Input value={editLoc.province || ""} onChange={(e) => setEditLoc({ ...editLoc, province: e.target.value })} />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input value={editLoc.postal_code || ""} onChange={(e) => setEditLoc({ ...editLoc, postal_code: e.target.value })} />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={editLoc.contact_phone || ""} onChange={(e) => setEditLoc({ ...editLoc, contact_phone: e.target.value })} />
              </div>
              <div className="col-span-2 flex items-center gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!editLoc.is_default}
                    onCheckedChange={(v) => setEditLoc({ ...editLoc, is_default: v })}
                    data-testid="loc-default-switch"
                  />
                  <Label>Default location</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!editLoc.sells_to_public}
                    onCheckedChange={(v) => setEditLoc({ ...editLoc, sells_to_public: v })}
                  />
                  <Label>Sells to public</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editLoc.active !== false}
                    onCheckedChange={(v) => setEditLoc({ ...editLoc, active: v })}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLoc(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={() => saveLocation(editLoc)}
              disabled={busy || !editLoc?.code || !editLoc?.name}
              data-testid="save-location-btn"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
